import * as React from 'react';
import { useState } from 'react';

import { translate } from '~l10n/client';
import { useAtom } from 'jotai';
import { RESET, atomWithReset } from 'jotai/utils';

import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useDeleteAccount } from '~teacher/hook/account';

import Constants from '../../../../../../../shared/Constants';

const isOpenAtom = atomWithReset(false);
isOpenAtom.onMount = (set) => {
  return () => {
    set(RESET);
  };
};

export function ConfirmDeleteAccountDialog() {
  const socrativeRouter = useSocrativeRouter();
  const deleteAccountMutation = useDeleteAccount();
  const [isOpen, setIsOpen] = useAtom(isOpenAtom);
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const onClose = () => {
    setIsOpen(RESET);
    setPassword('');
    setIsDeleting(false);
  };

  const isValidPassword =
    password.length >= Constants.MIN_PASSWORD_LENGTH &&
    password.length <= Constants.MAX_PASSWORD_LENGTH;

  const handleDeleteAccount = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isValidPassword) return;
    setIsDeleting(true);

    deleteAccountMutation.mutate(
      { password: password },
      {
        onSuccess: () => {
          socrativeRouter.routeToTeacherLogin();
        },
        onError: () => {
          setIsDeleting(false);
          toast.critical(
            translate('Something went wrong. Please contact support.')
          );
        },
      }
    );
  };

  return (
    <Dialog
      id="delete-account"
      open={isOpen}
      onClose={onClose}
      title={translate('Delete Account')}
    >
      <form
        className="flex flex-col font-sans text-base leading-8"
        onSubmit={handleDeleteAccount}
      >
        <div className="h-10 content-center">
          <Divider />
        </div>
        <div className="flex flex-col mt-4 gap-8">
          <p
            dangerouslySetInnerHTML={{
              __html: translate(
                'Deleting your account is <strong>permanent</strong> and <strong>can not be reversed</strong>.'
              ),
            }}
          />
          <p>
            {translate(
              'When you select DELETE ACCOUNT below, your quizzes, reports, rooms, rosters, and teacher account will be removed permanently. This information will NOT be recoverable.'
            )}
          </p>
        </div>
        <div className="h-10 content-center">
          <Divider />
        </div>
        <div className="flex flex-col gap-3">
          <label id="password-label">
            {translate('Your password is required to complete this process:')}
          </label>
          <TextInput
            aria-label="password-label"
            aria-describedby="password-input-description"
            autoComplete="off"
            maxLength={Constants.MAX_PASSWORD_LENGTH}
            required
            type="password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div>
          <div className="flex justify-end gap-4 mt-8">
            <SmallButton appearance="default" onClick={onClose}>
              {translate('Cancel')}
            </SmallButton>
            <SmallButton
              appearance="ghost-destructive"
              isDisabled={!isValidPassword}
              isLoading={isDeleting}
              type="submit"
            >
              {translate('Delete Account')}
            </SmallButton>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

ConfirmDeleteAccountDialog.useIsOpen = () => useAtom(isOpenAtom);
